// Purpose: admin system settings — feature flags, geo rules, config
// readback. Three tabs; feature flag toggles + geo rule saves are
// MFA-gated (enforced server-side; the global 428 interceptor redirects
// to /auth/step-up when required).
//
// Phase 7.5 port: URL-synced Tabs + Card-wrapped content + DataTable +
// Input/Toggle primitives.

import { useEffect, useState } from 'react';
import type { FeatureFlag, GeoRules, SafeConfigView } from '../../api/admin-settings.ts';
import {
  clearGeoRules,
  fetchConfig,
  fetchGeoRules,
  listFeatureFlags,
  setFeatureFlag,
  setGeoRules,
} from '../../api/admin-settings.ts';
import { formatDateTime } from '../../lib/format.ts';
import { PageHeader } from '../../components/nav/PageHeader.tsx';
import {
  Badge,
  Button,
  Card,
  DataTable,
  Input,
  Tabs,
  useTabs,
  type Column,
} from '../../components/ui/index.ts';

const TABS = [
  { id: 'flags', label: 'Feature flags' },
  { id: 'geo', label: 'Geo rules' },
  { id: 'system', label: 'System' },
];

export function Settings() {
  const { activeTab } = useTabs({ items: TABS });

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title="Settings" />
      <Tabs items={TABS} />
      {activeTab === 'flags' ? <FeatureFlagsTab /> : null}
      {activeTab === 'geo' ? <GeoRulesTab /> : null}
      {activeTab === 'system' ? <SystemTab /> : null}
    </div>
  );
}

function FeatureFlagsTab() {
  const [flags, setFlags] = useState<FeatureFlag[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [working, setWorking] = useState<string | null>(null);

  const load = async (): Promise<void> => {
    try {
      setFlags(await listFeatureFlags());
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const onToggle = async (flag: FeatureFlag): Promise<void> => {
    setWorking(flag.key);
    try {
      await setFeatureFlag(flag.key, !flag.enabled, flag.description);
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setWorking(null);
    }
  };

  const columns: Column<FeatureFlag>[] = [
    {
      key: 'key',
      header: 'Key',
      cell: (f) => <span className="font-mono text-body-s text-fg-primary">{f.key}</span>,
    },
    {
      key: 'description',
      header: 'Description',
      hideOnMobile: true,
      cell: (f) => <span className="text-body-s text-fg-secondary">{f.description}</span>,
    },
    {
      key: 'enabled',
      header: 'Enabled',
      width: '100px',
      cell: (f) =>
        f.enabled ? <Badge tone="success">Yes</Badge> : <Badge tone="neutral">No</Badge>,
    },
    {
      key: 'updated',
      header: 'Updated',
      hideOnMobile: true,
      cell: (f) => <span className="text-body-s text-fg-muted">{formatDateTime(f.updatedAt)}</span>,
    },
    {
      key: 'actions',
      header: '',
      width: '120px',
      cell: (f) => (
        <Button
          variant="secondary"
          size="sm"
          loading={working === f.key}
          onClick={() => void onToggle(f)}
        >
          {f.enabled ? 'Disable' : 'Enable'}
        </Button>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-3">
      {error ? (
        <div
          role="alert"
          className="px-4 py-3 rounded-md text-body-s border border-status-danger/30 bg-status-danger-muted text-status-danger"
        >
          {error}
        </div>
      ) : null}
      {flags.length === 0 ? (
        <p className="text-body-s text-fg-muted">No feature flags defined.</p>
      ) : (
        <DataTable<FeatureFlag>
          columns={columns}
          rows={flags}
          getRowKey={(f) => f.key}
          caption="Feature flags"
        />
      )}
    </div>
  );
}

function GeoRulesTab() {
  const [dropboxId, setDropboxId] = useState('');
  const [rules, setRules] = useState<GeoRules | null>(null);
  const [draft, setDraft] = useState<GeoRules>({
    allowedCountries: [],
    blockedCountries: [],
    anomalyThreshold: { distinctCountries: 5, windowMinutes: 60 },
  });
  const [error, setError] = useState<string | null>(null);
  const [working, setWorking] = useState(false);

  const onLoad = async (): Promise<void> => {
    if (!dropboxId.trim()) return;
    setWorking(true);
    setError(null);
    try {
      const r = await fetchGeoRules(dropboxId.trim());
      setRules(r);
      setDraft(
        r ?? {
          allowedCountries: [],
          blockedCountries: [],
          anomalyThreshold: { distinctCountries: 5, windowMinutes: 60 },
        }
      );
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setWorking(false);
    }
  };

  const onSave = async (): Promise<void> => {
    setWorking(true);
    setError(null);
    try {
      await setGeoRules(dropboxId.trim(), draft);
      await onLoad();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setWorking(false);
    }
  };

  const onClear = async (): Promise<void> => {
    setWorking(true);
    setError(null);
    try {
      await clearGeoRules(dropboxId.trim());
      await onLoad();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setWorking(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {error ? (
        <div
          role="alert"
          className="px-4 py-3 rounded-md text-body-s border border-status-danger/30 bg-status-danger-muted text-status-danger"
        >
          {error}
        </div>
      ) : null}

      <div className="flex flex-wrap items-end gap-3">
        <Input
          label="Dropbox ID"
          className="flex-1 min-w-[240px]"
          value={dropboxId}
          onChange={(e) => setDropboxId(e.target.value)}
        />
        <Button
          variant="secondary"
          size="md"
          loading={working}
          disabled={!dropboxId.trim()}
          onClick={() => void onLoad()}
        >
          Load
        </Button>
      </div>

      <Card>
        <div className="flex flex-col gap-4">
          <p className="text-body-s text-fg-secondary">
            {rules === null ? 'No rules set for this dropbox.' : 'Current rules loaded.'}
          </p>
          <Input
            label="Allowed countries (comma-separated, ISO-3166 alpha-2)"
            value={draft.allowedCountries.join(',')}
            onChange={(e) =>
              setDraft({
                ...draft,
                allowedCountries: e.target.value
                  .split(',')
                  .map((s) => s.trim().toUpperCase())
                  .filter(Boolean),
              })
            }
          />
          <Input
            label="Blocked countries"
            value={draft.blockedCountries.join(',')}
            onChange={(e) =>
              setDraft({
                ...draft,
                blockedCountries: e.target.value
                  .split(',')
                  .map((s) => s.trim().toUpperCase())
                  .filter(Boolean),
              })
            }
          />
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Anomaly distinctCountries"
              type="number"
              min={1}
              value={draft.anomalyThreshold.distinctCountries}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  anomalyThreshold: {
                    ...draft.anomalyThreshold,
                    distinctCountries: Number(e.target.value),
                  },
                })
              }
            />
            <Input
              label="Anomaly windowMinutes"
              type="number"
              min={1}
              value={draft.anomalyThreshold.windowMinutes}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  anomalyThreshold: {
                    ...draft.anomalyThreshold,
                    windowMinutes: Number(e.target.value),
                  },
                })
              }
            />
          </div>
          <div className="flex justify-end gap-2">
            {rules ? (
              <Button variant="danger" size="sm" loading={working} onClick={() => void onClear()}>
                Clear rules
              </Button>
            ) : null}
            <Button
              variant="primary"
              size="sm"
              loading={working}
              disabled={!dropboxId.trim()}
              onClick={() => void onSave()}
            >
              Save
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}

function SystemTab() {
  const [config, setConfig] = useState<SafeConfigView | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    fetchConfig()
      .then(setConfig)
      .catch((err) => setError((err as Error).message));
  }, []);

  if (error) return <p className="text-body-s text-status-danger">{error}</p>;
  if (!config) return <p className="text-body-s text-fg-muted">Loading config…</p>;

  return (
    <Card>
      <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-body-m">
        {Object.entries(config).map(([k, v]) => (
          <div key={k} className="contents">
            <dt className="font-mono text-body-s text-fg-muted">{k}</dt>
            <dd className="font-mono text-body-s text-fg-primary">{String(v)}</dd>
          </div>
        ))}
      </dl>
    </Card>
  );
}
